import {AxiosError} from "axios";
import {createContext, useCallback, useEffect, useMemo, useState} from 'react'
import {useNavigate} from "react-router-dom";
import {ApiConfig} from "../api/admin/ApiConfig";
import {AuthApi} from "../api/admin/generated";
import {useSpinnerStatusContext} from "../hook/UseSpinnerStatusContext";
import {logger} from "../utils/Logger";
import {Utils} from "../utils/Utils";

const authApi = new AuthApi(ApiConfig.config())

export interface IEffectivePermissionsContext {
    effectivePermissions: string[],
    clearEffectivePermissions: () => void,
}

export const EffectivePermissionsContext = createContext<IEffectivePermissionsContext>({
    effectivePermissions: [],
    clearEffectivePermissions: () => {},
});

interface EffectivePermissionsContextProviderProps {
    children: React.ReactNode;
}

/**
 * Provides the effective permissions of the current authenticated admin
 */
export function EffectivePermissionsContextProvider({children} : Readonly<EffectivePermissionsContextProviderProps>)
{
    const [effectivePermissions, setEffectivePermissions] = useState<string[]>([]);
    /**
     * Keeps track of activities for which the spinner should be shown. If an activity is started, the counter should
     * be increased by one and after the activity
     */
    const {setSpinnerStateCounter} = useSpinnerStatusContext();
    /**
     * Navigate is used to navigate to the login page if the admin is no longer authenticated
     */
    const navigate = useNavigate();

    function clearEffectivePermissions() {
        setEffectivePermissions([]);
    }

    const getEffectivePermissions = useCallback(() =>
    {
        logger.debug("Getting effective permissions")

        if (setSpinnerStateCounter !== undefined) {
            setSpinnerStateCounter(count => count + 1);
        }

        authApi.authControllerGetCallerEffectivePermissions().then(response =>
        {
            setEffectivePermissions(Array.from(response.data.values()));
        }).catch((err: Error | AxiosError) => {
            if (err instanceof AxiosError)
            {
                // do not log 401 errors
                if (err.response?.status !== 401) {
                    logger.error("Error getting effective permissions", err);
                }
            }

            clearEffectivePermissions()

            Utils.redirectToLoginIfNotAuthenticated(err, navigate);
        }).finally(() => {
            if (setSpinnerStateCounter !== undefined) {
                setSpinnerStateCounter(count => count - 1);
            }
        })
    }, [setSpinnerStateCounter, navigate])

    useEffect(() => {
        getEffectivePermissions();
    }, [getEffectivePermissions]);

    const exposedData = useMemo<IEffectivePermissionsContext>(() => {
        const effectivePermissionsContext : IEffectivePermissionsContext =
        {
            effectivePermissions : effectivePermissions,
            clearEffectivePermissions : clearEffectivePermissions
        }

        return effectivePermissionsContext;
    }, [effectivePermissions]);

    return(
        <EffectivePermissionsContext.Provider value = {exposedData}>
            {children}
        </EffectivePermissionsContext.Provider>
    )
}